import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useExternalOis, useOiListLive } from '@/lib/api/queries';
import type { DssOi, OiSummary } from '@/lib/api/types';
import { formatUtc } from '@/lib/format';
import { getMapCenter } from '@/lib/map/instance';
import { revealCurrentSearchArea } from '@/lib/map/layers/oiSearchArea';
import { isFutureOi } from '@/lib/oi/oiTime';
import { RTM_STATUSES, rtmStatusTone } from '@/lib/oi/rtmStatus';
import { type OiCategory, type OiStatusFilter, useOiStore } from '@/lib/store/oi';
import { cn } from '@/lib/utils';
import { useDeferredValue, useMemo } from 'react';
import { OiSearchHeader } from './OiSearchHeader';
import { EMPTY_COPY, filterOis } from './oiFilter';
import { oiStatusFill } from './oiStyle';
import { useNow } from './useNow';

// One chip per rtm_state status (ENG-213).
const FILTERS: readonly OiStatusFilter[] = RTM_STATUSES;

const CATEGORIES: { key: OiCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'internal', label: 'Internal' },
  { key: 'partner', label: 'Partner' },
  { key: '3rdparty', label: '3rd Party' },
];

/** ENG-191 — future OIs sit at 60 % until hovered/focused; 300 ms ease. */
const FUTURE_FADE = 'opacity-60 hover:opacity-100 focus-visible:opacity-100';
const CARD_TRANSITION =
  'transition-[color,background-color,border-color,opacity] duration-300 ease-in-out';

export function OIList({ onSelect }: { onSelect?: () => void }) {
  const { data, isPending, isError, refetch } = useOiListLive();
  const external = useExternalOis();
  const filters = useOiStore((s) => s.filters);
  const toggleFilter = useOiStore((s) => s.toggleFilter);
  const category = useOiStore((s) => s.category);
  const setCategory = useOiStore((s) => s.setCategory);
  const query = useOiStore((s) => s.searchQuery);
  const selectedHour = useOiStore((s) => s.selectedHour);
  const deferredQuery = useDeferredValue(query);
  const now = useNow();

  const {
    own,
    partnerTenants,
    external: thirdParty,
    emptyState,
  } = useMemo(
    () =>
      filterOis(
        { list: data ?? [], external: external.data ?? [] },
        { category, statusFilters: filters, query: deferredQuery, selectedHour, now },
      ),
    [data, external.data, category, filters, deferredQuery, selectedHour, now],
  );

  // Entering 3rd Party seeds the search box at the map centre (ENG-207).
  const chooseCategory = (c: OiCategory) => {
    const { searchArea, setSearchArea } = useOiStore.getState();
    if (c === '3rdparty' && !searchArea.center) setSearchArea({ center: getMapCenter() });
    setCategory(c);
    if (c === '3rdparty') revealCurrentSearchArea();
  };

  const is3rdParty = category === '3rdparty';
  // A disabled query is "pending" forever — only show skeletons while fetching.
  const loading = is3rdParty ? external.isPending && external.fetchStatus !== 'idle' : isPending;
  const failed = is3rdParty ? external.isError : isError;
  const showExternalHeader = partnerTenants.length > 0 || thirdParty.length > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <div className="text-[0.846em] font-bold">Operational Intent</div>
        {/* Mockup `#oi-filter-row` — provenance category, sits above the
            search and status chips. */}
        <div className="flex gap-1 mt-2 flex-wrap">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => chooseCategory(c.key)}
              className={cn(
                'px-2 h-6 rounded-full text-[0.615em] font-semibold tracking-wide transition-colors border',
                category === c.key
                  ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
                  : 'bg-(--color-surface) border-(--color-border) text-(--color-text3) hover:border-(--color-green)',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
        <OiSearchHeader />
        <div className="flex gap-1 mt-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => toggleFilter(f)}
              className={cn(
                'px-2 h-6 rounded-full text-[0.615em] font-semibold uppercase tracking-wider border transition-colors',
                filters[f]
                  ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
                  : 'bg-(--color-surface) border-(--color-border) text-(--color-text3) hover:border-(--color-green)',
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {loading &&
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        {failed && (
          <div className="text-[0.769em] text-(--color-red) text-center p-4 space-y-2">
            <div>
              {is3rdParty
                ? (external.error?.message ?? 'Failed to search the DSS.')
                : 'Failed to load OIs.'}
            </div>
            <button
              type="button"
              onClick={() => (is3rdParty ? external.refetch() : refetch())}
              className="text-(--color-green) underline"
            >
              Retry
            </button>
          </div>
        )}
        {!loading && !failed && emptyState && (
          <div className="text-[0.769em] text-(--color-text3) text-center py-6">
            {EMPTY_COPY[emptyState]}
          </div>
        )}
        {own.map((oi) => (
          <OiCard
            key={oi.oi_id}
            oi={oi}
            future={isFutureOi(oi.start_utc, now)}
            onSelect={onSelect}
          />
        ))}

        {/* External → Partner → {tenant} (mockup `_tpInjectOIs`): three
            text-only labels, each indented one level deeper, then the
            tenant's cards. 3rd Party sits alongside Partner under External. */}
        {showExternalHeader && (
          <div className="pt-[7px] px-0.5 pb-0.5 mt-0.5 border-t border-(--color-border-lite) text-[0.65em] font-extrabold uppercase tracking-[.8px] text-(--color-text3)">
            External
          </div>
        )}
        {partnerTenants.length > 0 && (
          <>
            <div className="pl-3.5 pt-1 pb-0.5 text-[0.6em] font-bold uppercase tracking-[.6px] text-(--color-text3)">
              Partner
            </div>
            {partnerTenants.map(([tenant, ois]) => (
              <div key={tenant} className="space-y-1.5">
                <div className="pl-[26px] pt-[3px] pb-0.5 text-[0.577em] font-semibold uppercase tracking-[.4px] text-(--color-text3)">
                  {tenant}
                </div>
                {ois.map((oi) => (
                  <PartnerOiCard
                    key={oi.oi_id}
                    oi={oi}
                    future={isFutureOi(oi.start_utc, now)}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            ))}
          </>
        )}
        {thirdParty.length > 0 && (
          <div className="pl-3.5 pt-1 pb-0.5 text-[0.6em] font-bold uppercase tracking-[.6px] text-(--color-text3)">
            3rd Party
          </div>
        )}
        {thirdParty.map((oi) => (
          <ThirdPartyOiCard
            key={oi.oi_id}
            oi={oi}
            future={isFutureOi(oi.start_utc, now)}
            now={now}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

/** Mockup `_injectDSSPoolCards` card: OI id + "operator · type"; 3rd Party
 *  tag, solid purple upper-cased DSS status and "In Window" stacked right. */
function ThirdPartyOiCard({
  oi,
  future,
  now,
  onSelect,
}: { oi: DssOi; future: boolean; now: number; onSelect?: () => void }) {
  const select = useOiStore((s) => s.select);
  const hover = useOiStore((s) => s.hover);
  return (
    <article
      tabIndex={0}
      role="button"
      data-category="3rdparty"
      onMouseEnter={() => hover(oi.oi_id)}
      onMouseLeave={() => hover(null)}
      onFocus={() => hover(oi.oi_id)}
      onBlur={() => hover(null)}
      onClick={() => {
        select(oi.oi_id);
        onSelect?.();
      }}
      className={cn(
        'rounded-md border border-(--color-border) border-t-2 border-t-(--color-purple) bg-(--color-surface) p-2.5 cursor-pointer hover:border-(--color-green) hover:bg-(--color-green-bg)',
        CARD_TRANSITION,
        future && FUTURE_FADE,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[0.846em] font-bold truncate">{oi.oi_id}</div>
          <div className="text-[0.692em] font-mono text-(--color-text3) mt-0.5 truncate">
            {oi.operator}&nbsp;·&nbsp;{oi.op_type}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge variant="partner" className="text-[0.615em] px-1.5 py-px">
            3rd Party
          </Badge>
          <span className="text-[0.615em] font-bold px-1.5 py-0.5 rounded-[3px] bg-(--color-purple) text-(--color-on-accent) whitespace-nowrap">
            {oi.status.toUpperCase()}
          </span>
          {inWindow(oi, now) && <InWindowBadge />}
        </div>
      </div>
    </article>
  );
}

function InWindowBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 text-[0.538em] font-bold px-1.5 py-0.5 rounded-[3px] border border-(--color-blue) bg-(--color-blue-bg) text-(--color-blue) whitespace-nowrap">
      ⏱ In Window
    </span>
  );
}

function OiCard({
  oi,
  future,
  onSelect,
}: { oi: OiSummary; future: boolean; onSelect?: () => void }) {
  const select = useOiStore((s) => s.select);
  const hover = useOiStore((s) => s.hover);
  const tone = rtmStatusTone(oi.status);
  return (
    <article
      tabIndex={0}
      role="button"
      onMouseEnter={() => hover(oi.oi_id)}
      onMouseLeave={() => hover(null)}
      onFocus={() => hover(oi.oi_id)}
      onBlur={() => hover(null)}
      onClick={() => {
        select(oi.oi_id);
        onSelect?.();
      }}
      className={cn(
        'rounded-md border border-(--color-border) bg-(--color-surface) p-2.5 cursor-pointer hover:border-(--color-green) hover:bg-(--color-green-bg) border-t-2',
        CARD_TRANSITION,
        future && FUTURE_FADE,
        tone === 'ok' && 'border-t-(--color-green)',
        tone === 'info' && 'border-t-(--color-blue)',
        tone === 'warn' && 'border-t-(--color-amber)',
        tone === 'crit' && 'border-t-(--color-red)',
        tone === 'muted' && 'border-t-(--color-text3)',
      )}
    >
      {/* Mockup card layout L4910-L4928: two-column flex —
                  left = oi_id / time range / aircraft + subscribers / updated;
                  right = the single RTM status badge (ENG-213). */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[0.846em] font-bold mb-0.5">{oi.oi_id}</div>
          <div className="text-[0.692em] font-mono text-(--color-text2) mb-1">
            {formatUtc(oi.start_utc)} – {formatUtc(oi.end_utc)}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[0.615em] font-semibold text-(--color-text2)">
              {oi.aircraft || '—'}
            </span>
            <span className="text-[0.615em] text-(--color-text3)">·</span>
            <span className="text-[0.615em] text-(--color-text3)">
              {oi.subscriber_count ?? 0} subscriber{(oi.subscriber_count ?? 0) === 1 ? '' : 's'}
            </span>
          </div>
          {oi.last_updated_utc && (
            <div className="text-[0.615em] font-mono text-(--color-text3) mt-1">
              Updated {formatUtc(oi.last_updated_utc)}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0 pt-0.5">
          <Badge variant={tone}>{oi.status}</Badge>
        </div>
      </div>
    </article>
  );
}

function inWindow(oi: { start_utc: string; end_utc: string }, nowMs: number): boolean {
  const start = Date.parse(oi.start_utc);
  const end = Date.parse(oi.end_utc);
  return Number.isFinite(start) && Number.isFinite(end) && start <= nowMs && nowMs <= end;
}

/** Mockup `_tpInjectOIs` partner card: title + "aircraft · op" on the left;
 *  Partner tag, solid status pill and "In Window" stacked on the right;
 *  tenant-coloured top border. */
function PartnerOiCard({
  oi,
  future,
  onSelect,
}: { oi: OiSummary; future: boolean; onSelect?: () => void }) {
  const select = useOiStore((s) => s.select);
  const hover = useOiStore((s) => s.hover);
  return (
    <article
      tabIndex={0}
      role="button"
      data-category="partner"
      onMouseEnter={() => hover(oi.oi_id)}
      onMouseLeave={() => hover(null)}
      onFocus={() => hover(oi.oi_id)}
      onBlur={() => hover(null)}
      onClick={() => {
        select(oi.oi_id);
        onSelect?.();
      }}
      className={cn(
        'rounded-md border border-(--color-border) border-t-2 border-t-(--color-partner) bg-(--color-surface) p-2.5 cursor-pointer hover:border-(--color-green) hover:bg-(--color-green-bg)',
        CARD_TRANSITION,
        future && FUTURE_FADE,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[0.846em] font-bold truncate">{oi.title || oi.oi_id}</div>
          <div className="text-[0.692em] font-mono text-(--color-text3) mt-0.5 truncate">
            {oi.aircraft}&nbsp;·&nbsp;{oi.op_type}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge variant="partner" className="text-[0.615em] px-1.5 py-px">
            Partner
          </Badge>
          <span
            className={cn(
              'text-[0.615em] font-bold px-1.5 py-0.5 rounded-[3px] text-(--color-on-accent) whitespace-nowrap',
              oiStatusFill(oi.status),
            )}
          >
            {oi.status}
          </span>
          {inWindow(oi, Date.now()) && <InWindowBadge />}
        </div>
      </div>
    </article>
  );
}
